/**
 * Rango Quantum — constantes y resolución de config.
 *
 * SAFE_DEFAULTS es la única fuente de verdad de defaults (patrón E.2 del
 * Standard); todo consumo de config pasa por resolveConfig() para que
 * ninguna clave llegue undefined aunque la config persistida sea parcial o
 * corrupta. Los defaults de los parámetros del MOTOR (slMode, tpDistances…,
 * leverage) son la config de serie CONGELADA (decisión de Marc, 2026-08-14
 * — docs/research/rango_quantum_estudio.md sección 16): 4H, 5x, TP
 * escalonado 5×ATR/6×ATR 60/40, sin SL técnico. El usuario puede ajustarlos
 * en el panel; "Restablecer valores Quantum" los recupera.
 */
import { CONFIG_SERIE_CONGELADA } from './engine.js'
import { KNOWN_FILTER_NAMES } from './filters.js'

export const QUANTUM_COLORS = Object.freeze({
  bull: '#1B5E20',
  bear: '#B71C1C',
  accent: '#B08D57',
  neutral: '#374151',
})

// Cesta del estudio (0.A/Fase 0.5, MISMA lista que
// backend/research/rango_quantum/*.py::BASKET_SYMBOLS) — las estadísticas
// del panel solo tienen validación out-of-sample para estos 12 símbolos;
// fuera de la cesta se muestran igual (nunca se ocultan números) pero con
// aviso explícito (ver stats.js::buildRecommendation).
export const BASKET_SYMBOLS = Object.freeze([
  'ZECUSDT', 'WLDUSDT', '1000PEPEUSDT', 'SUIUSDT', 'NEARUSDT', 'ARBUSDT',
  'UNIUSDT', 'FILUSDT', 'AAVEUSDT', 'ADAUSDT', 'AVAXUSDT', 'DOGEUSDT',
])

// Único timeframe que evalúa el motor de backend (rango_quantum_signal_tasks.py
// TIMEFRAME) — config de serie congelada. Fuera de 4H el motor JS del chart
// sigue calculando igual (mismo código, cualquier TF), pero sin la
// validación del estudio: aviso explícito, nunca ocultar los números.
export const VALIDATED_TIMEFRAME = '4h'

export const SL_MODES = Object.freeze(['liquidation', 'pct', 'atr'])
export const LEVERAGE_RANGE = Object.freeze({ min: 1, max: 20 })
export const SL_PCT_RANGE = Object.freeze({ min: 1, max: 30 }) // % de precio
export const SL_ATR_RANGE = Object.freeze({ min: 0.5, max: 6 }) // ×ATR
export const TP_ATR_RANGE = Object.freeze({ min: 0.5, max: 10 })
export const TP1_WEIGHT_RANGE = Object.freeze({ min: 10, max: 90 }) // % del tamaño original
// Time-stop de serie (Fase 0.6, decisión de Marc 2026-08-15): 0 = desactivado
// (comportamiento idéntico a antes de esta feature — salida pura TP/liquidación).
export const TIME_STOP_RANGE = Object.freeze({ min: 0, max: 120 })

export const SAFE_DEFAULTS = Object.freeze({
  _version: 2,

  enabled: false,

  // ── Visualización ──────────────────────────────────────────────────────
  showSignalLabels: true,
  showLevels: true, // líneas TP1/TP2/liquidación de la señal activa
  showLastSignals: true,

  // ── Notificaciones (F.2 — el envío real lo gobierna la task Celery de
  // backend/app/tasks/rango_quantum_signal_tasks.py; aquí solo se persisten
  // las preferencias del usuario) ──────────────────────────────────────────
  notificationsEnabled: false,
  notifyOnSignal: true,
  notifyOnTP1: true,
  notifyOnTP2: true,
  notifyOnLiquidation: true,

  // ── Dashboard / panel ─────────────────────────────────────────────────
  // Offset deliberado (no {x:8,y:8}): TODOS los demás indicadores del
  // registro comparten ese mismo default (auditoría 2026-08-14, Parte J del
  // Standard), así que cualquier combinación con otro indicador activo deja
  // el badge de rango_quantum completamente tapado. 360px de margen vertical
  // libra la altura de un badge completo (con señal activa + estadísticas +
  // últimas señales, ~300-320px medidos en H.2) — ver
  // docs/indicators/rango_quantum.md sección 3.
  badgePosition: { x: 8, y: 360 },
  panelSections: {},

  // ── Parámetros del motor — defaults = config de serie CONGELADA ────────
  leverage: CONFIG_SERIE_CONGELADA.leverage,
  slMode: CONFIG_SERIE_CONGELADA.slMode, // 'liquidation' | 'pct' | 'atr'
  slPct: 8, // usado solo si slMode === 'pct'
  slAtr: 3, // usado solo si slMode === 'atr'
  tp1DistanceAtr: CONFIG_SERIE_CONGELADA.tpDistancesAtr[0],
  tp2DistanceAtr: CONFIG_SERIE_CONGELADA.tpDistancesAtr[1],
  tp1WeightPct: Math.round(CONFIG_SERIE_CONGELADA.tpWeights[0] * 100),
  timeStopDays: CONFIG_SERIE_CONGELADA.timeStopDays,

  // ── Filtros de producción (WP H-G) ─────────────────────────────────────
  // Default = los dos filtros que aplican los bots (config de serie
  // congelada): el chart pinta lo que el bot operaría. Una lista vacía
  // explícita pinta la condición base sin filtros (comportamiento pre-H-G;
  // el badge de divergencia lo avisa si hay bot vivo).
  filters: [...CONFIG_SERIE_CONGELADA.filters],
})

function clampNumber(v, min, max, fallback) {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

function asBool(v, fallback) {
  return v == null ? fallback : Boolean(v)
}

function asEnum(v, allowed, fallback) {
  return allowed.includes(v) ? v : fallback
}

// Filtros de producción (WP H-G): solo nombres conocidos, sin duplicados.
// Un array vacío EXPLÍCITO se respeta (pintado sin filtros, avisa el badge);
// cualquier valor no-array cae al default (los dos filtros de prod).
function asFilterNames(v) {
  if (!Array.isArray(v)) return [...SAFE_DEFAULTS.filters]
  return [...new Set(v.filter((name) => KNOWN_FILTER_NAMES.includes(name)))]
}

/**
 * Config de fábrica para "Restablecer valores Quantum": NUNCA toca
 * `enabled` (el reset no apaga ni enciende el indicador — bug histórico en
 * otros indicadores) ni `badgePosition`/`panelSections` (el badge no salta
 * de sitio, los acordeones no se cierran).
 */
export function resetToDefaults(current) {
  const pos = current?.badgePosition
  return {
    ...SAFE_DEFAULTS,
    enabled: current?.enabled ?? SAFE_DEFAULTS.enabled,
    badgePosition: pos && Number.isFinite(pos.x) ? pos : SAFE_DEFAULTS.badgePosition,
    panelSections:
      current?.panelSections && typeof current.panelSections === 'object'
        ? current.panelSections
        : { ...SAFE_DEFAULTS.panelSections },
  }
}

/** Merge defensivo: defaults + config persistida + saneado por clave. */
export function resolveConfig(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...SAFE_DEFAULTS }
  }
  const merged = { ...SAFE_DEFAULTS, ...raw }
  return {
    ...merged,
    enabled: asBool(merged.enabled, false),
    showSignalLabels: asBool(merged.showSignalLabels, true),
    showLevels: asBool(merged.showLevels, true),
    showLastSignals: asBool(merged.showLastSignals, true),
    notificationsEnabled: asBool(merged.notificationsEnabled, false),
    notifyOnSignal: asBool(merged.notifyOnSignal, true),
    notifyOnTP1: asBool(merged.notifyOnTP1, true),
    notifyOnTP2: asBool(merged.notifyOnTP2, true),
    notifyOnLiquidation: asBool(merged.notifyOnLiquidation, true),
    badgePosition: merged.badgePosition && Number.isFinite(merged.badgePosition.x)
      ? merged.badgePosition
      : SAFE_DEFAULTS.badgePosition,
    panelSections: merged.panelSections && typeof merged.panelSections === 'object' ? merged.panelSections : {},
    leverage: Math.round(clampNumber(merged.leverage, LEVERAGE_RANGE.min, LEVERAGE_RANGE.max, SAFE_DEFAULTS.leverage)),
    slMode: asEnum(merged.slMode, SL_MODES, SAFE_DEFAULTS.slMode),
    slPct: clampNumber(merged.slPct, SL_PCT_RANGE.min, SL_PCT_RANGE.max, SAFE_DEFAULTS.slPct),
    slAtr: clampNumber(merged.slAtr, SL_ATR_RANGE.min, SL_ATR_RANGE.max, SAFE_DEFAULTS.slAtr),
    tp1DistanceAtr: clampNumber(merged.tp1DistanceAtr, TP_ATR_RANGE.min, TP_ATR_RANGE.max, SAFE_DEFAULTS.tp1DistanceAtr),
    tp2DistanceAtr: clampNumber(merged.tp2DistanceAtr, TP_ATR_RANGE.min, TP_ATR_RANGE.max, SAFE_DEFAULTS.tp2DistanceAtr),
    tp1WeightPct: Math.round(clampNumber(merged.tp1WeightPct, TP1_WEIGHT_RANGE.min, TP1_WEIGHT_RANGE.max, SAFE_DEFAULTS.tp1WeightPct)),
    timeStopDays: Math.round(clampNumber(merged.timeStopDays, TIME_STOP_RANGE.min, TIME_STOP_RANGE.max, SAFE_DEFAULTS.timeStopDays)),
    filters: asFilterNames(merged.filters),
  }
}
